import { useEffect, useState } from "react";
import { getAttributionBreakdown } from "services/marketing-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";
import { mapBreakdownRows, normalizeBreakdownRow } from "../utils/attributionDashboardUtils";
import AttributionBreakdownTable from "./AttributionBreakdownTable";

export default function AttributionBreakdownTables({ api, filters }) {
  const [byPlatform, setByPlatform] = useState([]);
  const [byAccount, setByAccount] = useState([]);
  const [byEntryPoint, setByEntryPoint] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setByPlatform([]);
      setByAccount([]);
      setByEntryPoint([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getAttributionBreakdown(api, { filters });
        if (!cancelled) {
          setByPlatform((result?.byPlatform ?? []).map(normalizeBreakdownRow));
          setByAccount((result?.byAccount ?? []).map(normalizeBreakdownRow));
          setByEntryPoint((result?.byEntryPoint ?? []).map(normalizeBreakdownRow));
        }
      } catch {
        if (!cancelled) {
          setByPlatform([]);
          setByAccount([]);
          setByEntryPoint([]);
          setError("Failed to load attribution breakdown. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [api, filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  const shared = { isLoading, error, filters };

  return (
    <>
      <AttributionBreakdownTable
        title="By Platform"
        columnLabel="Platform"
        rows={byPlatform}
        emptyMessage="No platform attribution data for the selected period."
        {...shared}
      />
      <AttributionBreakdownTable
        title="By Account"
        columnLabel="Account"
        rows={mapBreakdownRows(byAccount, "account")}
        emptyMessage="No account attribution data for the selected period."
        {...shared}
      />
      <AttributionBreakdownTable
        title="By Entry Point"
        columnLabel="Entry Point"
        rows={mapBreakdownRows(byEntryPoint, "entryPoint")}
        emptyMessage="No entry point attribution data for the selected period."
        {...shared}
      />
    </>
  );
}
